import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const FOSCollectionReports = () => {
  const [collections, setCollections] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    fosFilter: '',
    paymentMethod: '',
    collectionType: '',
    amountRange: { min: '', max: '' },
    reportType: 'detailed'
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Mock data focused on FOS collections
  const mockCollections = [
    {
      id: 'COL001',
      date: '2024-01-15',
      fosId: 'FOS001',
      fosName: 'Vikram Singh',
      fosPhone: '+91 9876543201',
      fosArea: 'North Delhi',
      retailerId: 'RET001',
      retailerName: 'Smart Mobile Store',
      amount: 25000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123456',
      collectionType: 'Outstanding Payment',
      commission: 625,
      commissionRate: 2.5,
      status: 'Completed',
      travelDistance: 12,
      travelTime: '45 mins',
      remarks: 'Smooth collection process'
    },
    {
      id: 'COL002',
      date: '2024-01-14',
      fosId: 'FOS002',
      fosName: 'Anita Desai',
      fosPhone: '+91 9876543202',
      fosArea: 'South Mumbai',
      retailerId: 'RET003',
      retailerName: 'Digital World',
      amount: 18000,
      paymentMethod: 'Bank Transfer',
      referenceNumber: 'TXN123457',
      collectionType: 'Outstanding Payment',
      commission: 360,
      commissionRate: 2.0,
      status: 'Completed',
      travelDistance: 8,
      travelTime: '30 mins',
      remarks: 'Quick digital payment'
    },
    {
      id: 'COL003',
      date: '2024-01-13',
      fosId: 'FOS001',
      fosName: 'Vikram Singh',
      fosPhone: '+91 9876543201',
      fosArea: 'North Delhi',
      retailerId: 'RET002',
      retailerName: 'Tech Zone Electronics',
      amount: 15000,
      paymentMethod: 'Cash',
      referenceNumber: '',
      collectionType: 'Outstanding Payment',
      commission: 375,
      commissionRate: 2.5,
      status: 'Completed',
      travelDistance: 15,
      travelTime: '60 mins',
      remarks: 'Cash collection completed'
    },
    {
      id: 'COL004',
      date: '2024-01-12',
      fosId: 'FOS003',
      fosName: 'Rohan Mehta',
      fosPhone: '+91 9876543203',
      fosArea: 'West Bangalore',
      retailerId: 'RET004',
      retailerName: 'Mobile Hub',
      amount: 12000,
      paymentMethod: 'Cheque',
      referenceNumber: 'CHQ001',
      collectionType: 'Advance Payment',
      commission: 264,
      commissionRate: 2.2,
      status: 'Pending',
      travelDistance: 20,
      travelTime: '75 mins',
      remarks: 'Cheque under verification'
    },
    {
      id: 'COL005',
      date: '2024-01-11',
      fosId: 'FOS002',
      fosName: 'Anita Desai',
      fosPhone: '+91 9876543202',
      fosArea: 'South Mumbai',
      retailerId: 'RET005',
      retailerName: 'Electronic Store',
      amount: 8000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123458',
      collectionType: 'Outstanding Payment',
      commission: 160,
      commissionRate: 2.0,
      status: 'Completed',
      travelDistance: 5,
      travelTime: '20 mins',
      remarks: 'Regular monthly collection'
    }
  ];

  const fosList = [
    { id: 'FOS001', name: 'Vikram Singh', area: 'North Delhi' },
    { id: 'FOS002', name: 'Anita Desai', area: 'South Mumbai' },
    { id: 'FOS003', name: 'Rohan Mehta', area: 'West Bangalore' }
  ];

  useEffect(() => {
    setCollections(mockCollections);
  }, []);

  const filteredData = useMemo(() => {
    let filtered = collections;

    // Date Range Filter
    if (filters.dateRange.start && filters.dateRange.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // FOS Filter
    if (filters.fosFilter) {
      filtered = filtered.filter(item => item.fosId === filters.fosFilter);
    }

    // Payment Method Filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(item => item.paymentMethod === filters.paymentMethod);
    }

    // Collection Type Filter
    if (filters.collectionType) {
      filtered = filtered.filter(item => item.collectionType === filters.collectionType);
    }

    // Amount Range Filter
    if (filters.amountRange.min) {
      filtered = filtered.filter(item => item.amount >= parseFloat(filters.amountRange.min));
    }
    if (filters.amountRange.max) {
      filtered = filtered.filter(item => item.amount <= parseFloat(filters.amountRange.max));
    }

    return filtered;
  }, [collections, filters]);

  const summaryData = useMemo(() => {
    const totalAmount = filteredData.reduce((sum, item) => sum + item.amount, 0);
    const totalCommission = filteredData.reduce((sum, item) => sum + item.commission, 0);
    const totalTravelDistance = filteredData.reduce((sum, item) => sum + item.travelDistance, 0);
    const completedCollections = filteredData.filter(item => item.status === 'Completed');
    const pendingCollections = filteredData.filter(item => item.status === 'Pending');

    const fosSummary = fosList.map(fos => {
      const fosCollections = filteredData.filter(item => item.fosId === fos.id);
      const fosTotal = fosCollections.reduce((sum, item) => sum + item.amount, 0);
      const fosCommission = fosCollections.reduce((sum, item) => sum + item.commission, 0);
      const fosDistance = fosCollections.reduce((sum, item) => sum + item.travelDistance, 0);
      return {
        ...fos,
        totalAmount: fosTotal,
        totalCommission: fosCommission,
        totalDistance: fosDistance,
        collectionCount: fosCollections.length,
        avgCommissionRate: fosCollections.length > 0 ? 
          (fosCommission / fosTotal * 100) : 0
      };
    });

    const paymentMethodSummary = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'DD'].map(method => {
      const methodCollections = filteredData.filter(item => item.paymentMethod === method);
      const methodTotal = methodCollections.reduce((sum, item) => sum + item.amount, 0);
      return {
        method,
        totalAmount: methodTotal,
        count: methodCollections.length
      };
    });

    return {
      totalAmount,
      totalCommission,
      totalTravelDistance,
      totalCollections: filteredData.length,
      completedAmount: completedCollections.reduce((sum, item) => sum + item.amount, 0),
      pendingAmount: pendingCollections.reduce((sum, item) => sum + item.amount, 0),
      avgCommissionRate: totalAmount > 0 ? (totalCommission / totalAmount * 100) : 0,
      fosSummary,
      paymentMethodSummary: paymentMethodSummary.filter(item => item.count > 0)
    };
  }, [filteredData, fosList]);

  const handleFilterChange = (category, field, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: typeof prev[category] === 'object' && !Array.isArray(prev[category])
        ? { ...prev[category], [field]: value }
        : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: {
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      fosFilter: '',
      paymentMethod: '',
      collectionType: '',
      amountRange: { min: '', max: '' },
      reportType: 'detailed'
    });
  };

  const generateReport = () => {
    const report = {
      summary: summaryData,
      collections: filteredData,
      filters: filters,
      generatedAt: new Date().toISOString()
    };
    setReportData(report);
    setShowPreviewModal(true);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheet = [
      ['FOS Collection Report Summary'],
      ['Generated On:', new Date().toLocaleDateString()],
      ['Period:', `${filters.dateRange.start} to ${filters.dateRange.end}`],
      [''],
      ['Total Collections:', summaryData.totalCollections],
      ['Total Amount Collected:', `₹${summaryData.totalAmount.toLocaleString()}`],
      ['Total Commission Earned:', `₹${summaryData.totalCommission.toLocaleString()}`],
      ['Average Commission Rate:', `${summaryData.avgCommissionRate.toFixed(2)}%`],
      ['Total Travel Distance:', `${summaryData.totalTravelDistance} km`],
      ['Completed Amount:', `₹${summaryData.completedAmount.toLocaleString()}`],
      ['Pending Amount:', `₹${summaryData.pendingAmount.toLocaleString()}`],
      [''],
      ['FOS Performance:'],
      ['FOS Name', 'Area', 'Collections', 'Amount', 'Commission', 'Travel Distance'],
      ...summaryData.fosSummary.map(fos => [
        fos.name,
        fos.area,
        fos.collectionCount,
        fos.totalAmount,
        fos.totalCommission,
        `${fos.totalDistance} km`
      ])
    ];

    const summaryWS = XLSX.utils.aoa_to_sheet(summarySheet);
    XLSX.utils.book_append_sheet(workbook, summaryWS, 'Summary');

    // Details Sheet
    const detailsWS = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      'Collection ID': item.id,
      'Date': item.date,
      'FOS Name': item.fosName,
      'FOS Phone': item.fosPhone,
      'FOS Area': item.fosArea,
      'Retailer': item.retailerName,
      'Amount Collected': item.amount,
      'Commission Earned': item.commission,
      'Commission Rate': `${item.commissionRate}%`,
      'Payment Method': item.paymentMethod,
      'Reference': item.referenceNumber,
      'Collection Type': item.collectionType,
      'Travel Distance': `${item.travelDistance} km`,
      'Travel Time': item.travelTime,
      'Status': item.status,
      'Remarks': item.remarks
    })));
    XLSX.utils.book_append_sheet(workbook, detailsWS, 'FOS Collections');

    XLSX.writeFile(workbook, `fos_collection_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('FOS collection report exported successfully');
  };

  const getStatusBadge = (status) => {
    return status === 'Completed' ? 
      <Badge bg="success">Completed</Badge> : 
      <Badge bg="warning">Pending</Badge>;
  };

  const getCommissionBadge = (rate) => {
    if (rate >= 2.5) return <Badge bg="success">{rate}%</Badge>;
    if (rate >= 2.0) return <Badge bg="info">{rate}%</Badge>;
    return <Badge bg="secondary">{rate}%</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-user-tie me-2"></i>
          FOS Collection Reports
        </h5>
        <Button variant="success" onClick={exportToExcel}>
          <i className="fas fa-file-excel me-2"></i>
          Export Excel
        </Button>
      </div>


      {/* Advanced Filters */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#031a6f' }}>
            <i className="fas fa-filter me-2"></i>
            Filter by FOS Performance
          </h5>
          <Row>
            <Col lg={4} md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Date Range</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => handleFilterChange('dateRange', 'start', e.target.value)}
                  />
                  <Form.Control
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => handleFilterChange('dateRange', 'end', e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Form.Group>
                <Form.Label>Select FOS</Form.Label>
                <Form.Select
                  value={filters.fosFilter}
                  onChange={(e) => handleFilterChange('fosFilter', null, e.target.value)}
                >
                  <option value="">All FOS</option>
                  {fosList.map(fos => (
                    <option key={fos.id} value={fos.id}>{fos.name} - {fos.area}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', null, e.target.value)}
                >
                  <option value="">All Methods</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="DD">Demand Draft</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={2} md={4} className="mb-3">
              <Form.Group>
                <Form.Label>Collection Type</Form.Label>
                <Form.Select
                  value={filters.collectionType}
                  onChange={(e) => handleFilterChange('collectionType', null, e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Outstanding Payment">Outstanding Payment</option>
                  <option value="Advance Payment">Advance Payment</option>
                  <option value="Regular Payment">Regular Payment</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={1} md={4} className="mb-3 d-flex align-items-end">
              <Button 
                variant="outline-secondary" 
                onClick={clearFilters} 
                className="w-100"
                title="Clear Filters"
              >
                <i className="fas fa-eraser"></i>
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Collections Table */}
      <h5 className="mb-3" style={{ color: '#031a6f' }}>
        <i className="fas fa-table me-2"></i>
        FOS Collections Data ({filteredData.length} records)
      </h5>
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Collection ID</th>
              <th>FOS Details</th>
              <th>Retailer</th>
              <th>Amount</th>
              <th>Commission</th>
              <th>Travel Info</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((collection) => (
              <tr key={collection.id}>
                <td>{collection.date}</td>
                <td><strong>{collection.id}</strong></td>
                <td>
                  <div>
                    <strong>{collection.fosName}</strong>
                    <br />
                    <small className="text-muted">{collection.fosArea}</small>
                    <br />
                    <small className="text-muted">{collection.fosPhone}</small>
                  </div>
                </td>
                <td>{collection.retailerName}</td>
                <td className="text-primary fw-bold">₹{collection.amount.toLocaleString()}</td>
                <td>
                  <div>
                    <span className="text-success fw-bold">₹{collection.commission.toLocaleString()}</span>
                    <br />
                    {getCommissionBadge(collection.commissionRate)}
                  </div>
                </td>
                <td>
                  <div>
                    <span>{collection.travelDistance} km</span>
                    <br />
                    <small className="text-muted">{collection.travelTime}</small>
                  </div>
                </td>
                <td>{getStatusBadge(collection.status)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Report Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>FOS Collection Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportData && (
            <div>
              <div className="text-center mb-4">
                <h4>FOS Collection Report</h4>
                <p className="text-muted">
                  Period: {filters.dateRange.start} to {filters.dateRange.end}
                </p>
              </div>

              <Row className="mb-4">
                <Col md={6}>
                  <h6>Summary</h6>
                  <ul className="list-unstyled">
                    <li>Total Collections: <strong>{reportData.summary.totalCollections}</strong></li>
                    <li>Total Amount: <strong>₹{reportData.summary.totalAmount.toLocaleString()}</strong></li>
                    <li>Total Commission: <strong>₹{reportData.summary.totalCommission.toLocaleString()}</strong></li>
                    <li>Average Commission Rate: <strong>{reportData.summary.avgCommissionRate.toFixed(2)}%</strong></li>
                    <li>Total Travel Distance: <strong>{reportData.summary.totalTravelDistance} km</strong></li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>FOS Performance</h6>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>FOS</th>
                        <th>Collections</th>
                        <th>Amount</th>
                        <th>Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.fosSummary
                        .filter(fos => fos.collectionCount > 0)
                        .map((fos, index) => (
                        <tr key={index}>
                          <td>{fos.name}</td>
                          <td>{fos.collectionCount}</td>
                          <td>₹{fos.totalAmount.toLocaleString()}</td>
                          <td>₹{fos.totalCommission.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-download me-2"></i>
            Export to Excel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FOSCollectionReports;